import React, {
  ChangeEvent,
  ComponentProps,
  memo,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react'

import styled from '@emotion/styled'

import {
  useHandler,
  useInfiniteScrollContainer,
  useOnClickOutside,
} from '@surglogs-app/hooks'
import { Info } from '@surglogs-app/icons'
import { WrapInToolTip } from '@surglogs-app/ui.tooltip'

import { Box } from '../Box'
import { Button } from '../Button'
import { Checkbox } from '../Checkbox'
import { CircleLoader } from '../CircleLoader'
import { DropdownIcon } from '../DropdownIcon'
import { FlexBox } from '../FlexBox'
import { Icon } from '../Icon'
import { Popup } from '../Popup'
import { SearchInput } from '../SearchInput'
import { Text } from '../Text'
import { TextButton } from '../TextButton'

const POPUP_MIN_WIDTH = 230
const POPUP_MAX_WIDTH = 250
const POPUP_CONTENT_MAX_HEIGHT = 250

const StyledContainer = styled(FlexBox)`
  flex-direction: column;
  gap: ${({ theme }) => theme.ui.spacings.sm};
  padding-block: ${({ theme }) => theme.ui.spacings['abs.12']};
  padding-inline: ${({ theme }) => theme.ui.spacings['abs.12']};
  overflow-y: scroll;
  max-height: ${POPUP_CONTENT_MAX_HEIGHT}px;
  min-height: ${({ theme }) => theme.ui.spacings.xl};
  position: relative;
`

const PopupContainer = (props: ComponentProps<typeof StyledContainer>) => (
  <StyledContainer
    {...{
      'aria-label': 'filter-select-menu',
      role: 'menu',
      ...props,
    }}
  />
)

const StyledLoader = styled(CircleLoader)`
  margin-left: auto;
`

const StyledDropdownIcon = styled(DropdownIcon)`
  margin-left: auto;
`

type FilterGroup = {
  label?: string
  items: FilterItem[] | FilterGroup[]
}

export type FilterItem = Omit<ComponentProps<typeof Checkbox>, 'onChange'> & {
  label: string
  onChange?: (e?: ChangeEvent<HTMLInputElement>) => void
}

interface FilterSelectProps
  extends Omit<ComponentProps<typeof Box>, 'ref' | 'title' | 'children'>,
    Partial<Omit<Params, 'scrollTopOffsetRef'>> {
  items: FilterGroup[] | FilterItem[]
  onClear?: () => void
  selectedItemsCount: number
  isClearable?: boolean

  /* Labels */
  label?: string
  labelTooltip?: string
  searchPlaceholder?: string
  buttonLabel: string
  selectedLabel?: string
  clearButtonLabel?: string
  noResultsLabel?: string

  /* Async filter */
  search?: string
  onSearchChange?: (value: string) => void
  isFetching?: boolean

  /* Additional props */
  buttonProps?: ComponentProps<typeof Button>
  alignMenu?: ComponentProps<typeof Popup>['alignMenu']
  withBodyScrollLock?: boolean
  buttonMinWidth?: ComponentProps<typeof Button>['miw']
}

type Params = Parameters<typeof useInfiniteScrollContainer>[0]

interface InfiniteScrollContainerProps extends Params {
  children: ReactNode
}

const InfiniteScrollContainer = ({
  children,
  isLoading,
  onNextPage,
  hasMore,
  scrollTopOffsetRef,
}: InfiniteScrollContainerProps) => {
  const { containerRef, onScroll } = useInfiniteScrollContainer({
    isLoading,
    onNextPage,
    scrollTopOffsetRef,
    hasMore,
  })

  return (
    <PopupContainer {...{ ref: containerRef, onScroll }}>
      {children}
    </PopupContainer>
  )
}

const isFilterGroup = (item: FilterGroup | FilterItem): item is FilterGroup => {
  return 'items' in item && Array.isArray(item.items)
}

const renderFilterItem = (
  { checked, onChange, label, ...rest }: FilterItem,
  key: string,
): ReactNode => {
  return (
    <Checkbox
      key={key}
      {...{
        checked,
        onChange,
        size: 'mini',
        w: 'max-content',
        maw: '100%',
        ...rest,
      }}>
      {label}
    </Checkbox>
  )
}

const renderFilterGroup = (
  items: FilterGroup[] | FilterItem[],
  parentKey: string,
): ReactNode => {
  return items.map((item, index) => {
    const key = `${parentKey}-${index}`

    if (isFilterGroup(item)) {
      return (
        <FlexBox key={`${key}-group`} {...{ fd: 'column', gap: 'sm' }}>
          <Text
            {...{ bold: true, size: 'xxs', mt: index > 0 ? 'sm' : undefined }}>
            {item.label}
          </Text>
          {renderFilterGroup(item.items, key)}
        </FlexBox>
      )
    }

    return renderFilterItem(item, String(index))
  })
}

const FilterSelectLabel = memo(
  ({
    label,
    labelTooltip,
  }: Pick<FilterSelectProps, 'label' | 'labelTooltip'>) => {
    if (!label) return null

    if (labelTooltip) {
      return (
        <FlexBox {...{ ai: 'center', gap: 'xs' }}>
          <Text {...{ bold: true, size: 'xxs' }}>{label}</Text>
          <WrapInToolTip {...{ tooltipText: labelTooltip }}>
            <Icon
              {...{
                icon: Info,
                variant: 'primary',
                size: 'md',
                cursor: 'pointer',
              }}
            />
          </WrapInToolTip>
        </FlexBox>
      )
    }

    return <Text {...{ bold: true, size: 'xxs' }}>{label}</Text>
  },
)

export const FilterSelect = memo(
  ({
    label,
    labelTooltip,
    onSearchChange,
    items,
    searchPlaceholder,
    buttonLabel: buttonLabelProp,
    selectedLabel,
    isLoading,
    isFetching,
    onNextPage,
    hasMore,
    onClear,
    clearButtonLabel,
    noResultsLabel,
    search,
    buttonProps,
    alignMenu = 'left',
    selectedItemsCount,
    isClearable: isClearableProp,
    withBodyScrollLock = true,
    buttonMinWidth = POPUP_MIN_WIDTH,
    ...rest
  }: FilterSelectProps) => {
    const scrollTopOffsetRef = useRef<number>(0)
    const [isExpanded, setIsExpanded] = useState(false)
    const wrapperRef = useRef<HTMLButtonElement>(null)

    const togglePopup = useHandler(() => {
      setIsExpanded((prev) => !prev)
    })

    const closePopup = useHandler(() => {
      setIsExpanded(false)
    })

    useOnClickOutside(wrapperRef, closePopup, undefined, 'click')

    const buttonLabel = useMemo(() => {
      if (selectedItemsCount > 0) {
        return `${selectedItemsCount} ${selectedLabel}`
      }

      return buttonLabelProp
    }, [buttonLabelProp, selectedLabel, selectedItemsCount])

    const isClearable = isClearableProp ?? selectedItemsCount > 0
    const isSearchInputVisible = !!onSearchChange

    const content = useMemo(() => {
      if (isLoading) {
        return (
          <FlexBox {...{ jc: 'center' }}>
            <CircleLoader {...{ size: 'md', variant: 'default' }} />
          </FlexBox>
        )
      }

      if (items.length === 0) {
        return (
          <Text {...{ size: 'xxs', variant: 'navy.50', ta: 'center' }}>
            {noResultsLabel}
          </Text>
        )
      }

      return items.map((item, index) => {
        if (isFilterGroup(item)) {
          return (
            <FlexBox key={index} {...{ fd: 'column', maw: '300px', gap: 'sm' }}>
              {item.label ? (
                <Text {...{ bold: true, size: 'xxs' }}>{item.label}</Text>
              ) : null}

              {renderFilterGroup(item.items, String(index))}
            </FlexBox>
          )
        }

        return renderFilterItem(item, String(index))
      })
    }, [isLoading, items, noResultsLabel])

    return (
      <Box {...{ flex: 1, ...rest }}>
        <FlexBox {...{ fd: 'column', gap: 'sm' }}>
          <FilterSelectLabel {...{ label, labelTooltip }} />

          <Button
            {...{
              onClick: togglePopup,
              ref: wrapperRef,
              variant: 'secondary',
              miw: buttonMinWidth,
              jc: 'space-between',
              size: 'mini',
              ...buttonProps,
            }}>
            {buttonLabel}

            {isFetching ? (
              <StyledLoader {...{ variant: 'default', size: 'abs.12' }} />
            ) : (
              <StyledDropdownIcon {...{ isExpanded, w: 'auto', h: 'auto' }} />
            )}
          </Button>
        </FlexBox>

        {isExpanded ? (
          <Popup
            {...{
              wrapperRef,
              onClose: closePopup,
              alignMenu,
              miw: POPUP_MIN_WIDTH,
              maw: POPUP_MAX_WIDTH,
              withBodyScrollLock,
            }}>
            <FlexBox {...{ fd: 'column', px: 'sm' }}>
              {onSearchChange ? (
                <Box {...{ py: 'sm' }}>
                  <SearchInput
                    {...{
                      onChange: onSearchChange,
                      placeholder: searchPlaceholder,
                      size: 'mini',
                      value: search,
                    }}
                  />
                </Box>
              ) : null}

              <FlexBox
                {...{
                  jc: 'space-between',
                  ai: 'center',
                  gap: 'sm',
                  px: 'xs',
                  pb: 'sm',
                  pt: isSearchInputVisible ? undefined : 'sm',
                }}>
                <Text {...{ size: 'xxs', variant: 'navy.50' }}>
                  {`${selectedItemsCount} ${selectedLabel}`}
                </Text>

                {onClear && clearButtonLabel ? (
                  <TextButton
                    {...{
                      size: 'xxs',
                      variant: 'default',
                      onClick: onClear,
                      disabled: !isClearable,
                    }}>
                    {clearButtonLabel}
                  </TextButton>
                ) : null}
              </FlexBox>
            </FlexBox>

            <Box {...{ h: '1px', w: '100%', bgc: 'navy.10' }} />

            {onNextPage ? (
              <InfiniteScrollContainer
                {...{
                  onNextPage,
                  hasMore,
                  scrollTopOffsetRef,
                  isLoading: isFetching,
                }}>
                {content}
              </InfiniteScrollContainer>
            ) : (
              <PopupContainer>{content}</PopupContainer>
            )}
          </Popup>
        ) : null}
      </Box>
    )
  },
)
